import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { ConsentForm, SignedConsent, signConsent } from "../../services/clientRecordsService";
import SignaturePad from "./SignaturePad";

interface Props {
  clientId: string;
  form: ConsentForm;
  /** Pre-filled signer name (the client's own). */
  defaultName?: string;
  onSigned: (consent: SignedConsent) => void;
  onCancel?: () => void;
}

/**
 * One consent form to sign: the text, the signer's name, and (when the form
 * asks for it) a drawn signature. What is shown here is what gets stored.
 */
const ConsentSignPanel: React.FC<Props> = ({ clientId, form, defaultName = "", onSigned, onCancel }) => {
  const [name, setName] = useState(defaultName);
  const [signature, setSignature] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = name.trim().length > 0 && (!form.requiresSignature || Boolean(signature));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      onSigned(
        await signConsent(clientId, {
          consentFormId: form.id,
          signedName: name.trim(),
          ...(form.requiresSignature && signature ? { signature } : {}),
        })
      );
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError((Array.isArray(message) ? message[0] : message) || "Could not save the signature.");
    }
    setSaving(false);
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{form.title}</Typography>
      <Box
        sx={{
          maxHeight: 220,
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          fontSize: "0.9rem",
          lineHeight: 1.6,
          p: 1.5,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "#FAFBFC",
        }}
      >
        {form.body}
      </Box>
      <TextField
        label="Signed by (full name)"
        size="small"
        fullWidth
        value={name}
        onChange={(e) => setName(e.target.value)}
        inputProps={{ maxLength: 120 }}
        sx={{ mb: 2 }}
      />
      {form.requiresSignature && <SignaturePad onChange={setSignature} />}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1.5 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          disableElevation
          disabled={!ready || saving}
          onClick={submit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Agree and sign
        </Button>
      </Box>
    </Box>
  );
};

export default ConsentSignPanel;
